import React, {useEffect, useState} from "react";
import {useGlobalContext} from "../context/GlobalState";
import {
    DataGridPro,
    useGridApiRef,
    gridFilteredSortedRowIdsSelector,
    GridToolbarContainer,
    GridToolbarExport,
    GridToolbarDensitySelector,
    GridToolbarFilterButton,
    GridToolbarColumnsButton,
    getGridStringOperators,
    getGridNumericOperators,
    getGridDateOperators,
    GridFilterInputMultipleValue,
    GridFilterInputValue
} from "@mui/x-data-grid-pro";
import {getAvailableBranches, getBranchTerminalIds} from "../services/Request";
import {ReadableLocaleDate, ToCurrency} from "../services/Utils";
import {useTheme} from "@mui/material/styles";
import {Box, Checkbox, InputLabel, ListItemText, MenuItem, Select} from "@mui/material";
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import PaymentsIcon from '@mui/icons-material/Payments';
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';
import Tooltip from "@mui/material/Tooltip";
import LockPersonIcon from '@mui/icons-material/LockPerson';
import TrueChecksActionCell from "./TrueChecksActionCell";
import Warning from "@mui/icons-material/Warning";
import {ErrorOutlineOutlined} from "@mui/icons-material";
import {DatePicker} from "@mui/x-date-pickers/DatePicker";
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from "dayjs";


const ItemTypeComponent = ({type}) => {
    const theme = useTheme();
    let Icon;
    let hoverTxt;
    switch (type) {
        case "00":
            Icon = AttachMoneyIcon;
            hoverTxt = "Cash";
            break;
        case "03":
            Icon = PaymentsIcon;
            hoverTxt = "Check";
            break;
        default:
            Icon = QuestionMarkIcon;
            hoverTxt = "Unknown";
            break;
    }
    return (
        <Box display="flex" alignItems="center" justifyContent="center" height="100%">
            <Tooltip title={hoverTxt} arrow placement="top">
                <Icon style={{color: theme.palette.primary.main}}/>
            </Tooltip>
        </Box>
    );
};

const ThemedLockPersonIcon = () => {
    const theme = useTheme();
    return (
        <LockPersonIcon sx={{color: theme.palette.primary.main}}/>
    );
}

function ItemTypeSelectFilter(props) {
    const {item, applyValue} = props;

    const handleFilterChange = (event) => {
        applyValue({...item, value: event.target.value});
    };

    const validValues = ["00", "03"];
    const value = validValues.includes(item.value) ? item.value : "";

    return (
        <Box
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                height: 48,
            }}
        >
            <InputLabel id="item-type-select-label">Item Type</InputLabel>
            <Select
                labelId="item-type-select-label"
                id="item-type-select"
                value={value}
                label="Item Type"
                fullWidth
                onChange={handleFilterChange}
                variant="standard"
            >
                <MenuItem value={"00"}>Cash</MenuItem>
                <MenuItem value={"03"}>Check</MenuItem>
            </Select>
        </Box>
    );
}

function TrueChecksSingleSelectFilter(props) {
    const {item, applyValue} = props;

    const handleFilterChange = (event) => {
        applyValue({...item, value: event.target.value});
    };

    const trueChecksActions = [
        'None',
        'Deny',
        'Hold',
        'Extended Hold',
        'Insufficient Data',
        'Review Manually'
    ]

    return (
        <Box
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                height: 48,
            }}
        >
            <InputLabel id="truechecks-single-select-label">TrueChecks® Action</InputLabel>
            <Select
                labelId="truechecks-single-select-label"
                id="truechecks-single-select"
                value={item.value || []}
                label="TrueChecks® Action"
                variant="standard"
                fullWidth
                onChange={handleFilterChange}
            >
                {trueChecksActions?.map((action) => (
                    <MenuItem key={action} value={action}>
                        {action}
                    </MenuItem>
                ))}
            </Select>
        </Box>
    );
}

function TrueChecksMultiSelectFilter(props) {
    const {item, applyValue} = props;

    const handleFilterChange = (event) => {
        applyValue({...item, value: event.target.value});
    };

    const trueChecksActions = [
        'None',
        'Deny',
        'Hold',
        'Extended Hold',
        'Insufficient Data',
        'Review Manually'
    ]

    return (
        <Box
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                height: 48,
            }}
        >
            <InputLabel id="truechecks-multi-select-label">TrueChecks® Action</InputLabel>
            <Select
                labelId="truechecks-multi-select-label"
                id="truechecks-multi-select"
                value={item.value || []}
                label="TrueChecks® Action"
                variant="standard"
                fullWidth
                multiple
                onChange={handleFilterChange}
                renderValue={(selected) => selected.join(', ')}
            >
                {trueChecksActions?.map((action) => (
                    <MenuItem key={action} value={action}>
                        <Checkbox checked={item?.value?.indexOf(action) > -1}/>
                        <ListItemText primary={action}/>
                    </MenuItem>
                ))}
            </Select>
        </Box>
    );
}

function TerminalIdSingleSelectFilter(props) {
    const {item, applyValue} = props;
    const [terminalIds, setTerminalIds] = useState([]);
    const checkData = useGlobalContext();

    useEffect(() => {
        const fetchTerminalIds = async () => {
            try {
                const data = getDistinctFilters(checkData,'terminalId');
                setTerminalIds(data);
            } catch (error) {
                console.error(error)
            }
        };

        fetchTerminalIds();
    }, []);

    const handleFilterChange = (event) => {
        applyValue({...item, value: event.target.value});
    };

    return (
        <Box
            sx={{
                display: 'inline-flex',
                flexDirection: 'row',
                alignItems: 'center',
                height: 48,
            }}
        >
            <InputLabel id="terminal-id-select-label">Terminal ID</InputLabel>
            <Select
                labelId="terminal-id-select-label"
                id="terminal-id-select"
                value={item.value ?? ''}
                label="Terminal ID"
                fullWidth
                onChange={handleFilterChange}
                variant="standard"
            >
                {terminalIds?.map((terminal) => (
                    <MenuItem key={terminal} value={terminal}>
                        {terminal}
                    </MenuItem>
                ))}
            </Select>
        </Box>
    );
}

function TerminalIdMultiSelectFilter(props) {
    const {item, applyValue} = props;
    const [terminalIds, setTerminalIds] = useState([]);
    const checkData = useGlobalContext();

    useEffect(() => {
        const fetchTerminalIds = async () => {
            try {
                const data = getDistinctFilters(checkData,'terminalId');
                setTerminalIds(data);
            } catch (error) {
                console.error(error)
            }
        };

        fetchTerminalIds();
    }, []);

    const handleFilterChange = (event) => {
        applyValue({...item, value: event.target.value});
    };

    return (
        <Box
            sx={{
                display: 'inline-flex',
                flexDirection: 'row',
                alignItems: 'center',
                height: 48,
            }}
        >
            <InputLabel id="terminal-id-select-label">Terminal ID</InputLabel>
            <Select
                labelId="terminal-id-select-label"
                id="terminal-id-select"
                value={item.value ?? []}
                label="Terminal ID"
                fullWidth
                multiple
                onChange={handleFilterChange}
                renderValue={(selected) => selected.join(', ')}
                variant="standard"
            >
                {terminalIds?.map((terminal) => (
                    <MenuItem key={terminal} value={terminal}>
                        <Checkbox checked={item?.value?.indexOf(terminal) > -1}/>
                        <ListItemText primary={terminal}/>
                    </MenuItem>
                ))}
            </Select>
        </Box>
    );
}

function BranchSingleSelectFilter({item, applyValue}) {
    const [branches, setBranches] = useState([]);
    const checkData = useGlobalContext();

    useEffect(() => {
        const fetchBranches = async () => {
            try {
                const response = await getAvailableBranches();
                const data = getDistinctFilters(checkData,'branch');
                setBranches(response.filter((branch) => data.includes(branch.name)));
            } catch (error) {
                console.error(error);
            }
        };
        fetchBranches();
    }, []);

    const handleFilterChange = (event) => {
        applyValue({...item, value: event.target.value});
    };

    return (
        <Box sx={{display: 'inline-flex', alignItems: 'center', height: 48}}>
            <InputLabel id="branch-select-label">Branch</InputLabel>
            <Select
                labelId="branch-select-label"
                id="branch-select"
                value={item.value ?? ''}
                label="Branch"
                fullWidth
                onChange={handleFilterChange}
                variant="standard"
            >
                {branches.map((branch) => (
                    <MenuItem key={branch.id} value={branch.name}>
                        {branch.name}
                    </MenuItem>
                ))}
            </Select>
        </Box>
    );
}
function getDistinctFilters(checkData, key){
    let distinct = [];
    let unique = {};
    checkData.checkData.forEach(function (x) {
        if (!unique[x[key]]) {
            distinct.push(x[key]);
            unique[x[key]] = true;
        }
    });
    return distinct;
}
function BranchMultiSelectFilter({item, applyValue}) {
    const [branches, setBranches] = useState([]);
    const checkData = useGlobalContext();

    useEffect(() => {
        const fetchBranches = async () => {
            try {
                const response = await getAvailableBranches();
                const data = getDistinctFilters(checkData,'branch');
                setBranches(response.filter((branch) => data.includes(branch.name)));
            } catch (error) {
                console.error(error);
            }
        };
        fetchBranches();
    }, []);

    const handleFilterChange = (event) => {
        applyValue({...item, value: event.target.value});
    };

    return (
        <Box sx={{display: 'inline-flex', alignItems: 'center', height: 48}}>
            <InputLabel id="branch-multi-select-label">Branch</InputLabel>
            <Select
                labelId="branch-multi-select-label"
                id="branch-multi-select"
                value={item.value ?? []}
                label="Branch"
                fullWidth
                multiple
                onChange={handleFilterChange}
                variant="standard"
                renderValue={(selected) => selected.join(', ')}
            >
                {branches.map((branch) => (
                    <MenuItem key={branch.id} value={branch.name}>
                        <Checkbox checked={item.value?.indexOf(branch.name) > -1}/>
                        <ListItemText primary={branch.name}/>
                    </MenuItem>
                ))}
            </Select>
        </Box>
    );
}

const StringSingleSelectFilter = (props) => {
    const {item, applyValue, column, columnValues} = props;

    const handleFilterChange = (event) => {
        applyValue({...item, value: event.target.value});
    };

    return (
        <Box
            sx={{
                display: 'inline-flex',
                flexDirection: 'row',
                alignItems: 'center',
                height: 48,
            }}
        >
            <InputLabel id={`${column}-id-select-label`}>{column}</InputLabel>
            <Select
                value={item.value ?? ''}
                onChange={handleFilterChange}
                displayEmpty
                variant="standard"
                fullWidth
            >
                {columnValues.map((option) => (
                    <MenuItem key={option} value={option}>
                        {option}
                    </MenuItem>
                ))}
            </Select>
        </Box>
    );
};

function ReviewStatusMultiSelectFilter(props) {
    const {item, applyValue} = props;
    const [options, setOptions] = useState([]);

    useEffect(() => {
        setOptions(['Pending', 'Approved', 'Rejected']);
    }, []);

    const handleFilterChange = (event) => {
        applyValue({...item, value: event.target.value});
    };

    return (
        <Box
            sx={{
                display: 'inline-flex',
                flexDirection: 'row',
                alignItems: 'center',
                height: 48,
            }}
        >
            <InputLabel id="review-select-label">Review</InputLabel>
            <Select
                labelId="review-select-label"
                id="review-select"
                value={item.value ?? []}
                label="Review"
                onChange={handleFilterChange}
                variant="standard"
                fullWidth
                multiple
                renderValue={(selected) => selected.join(', ')}
            >
                {options.map((option) => (
                    <MenuItem key={option} value={option}>
                        <Checkbox checked={item?.value?.indexOf(option) > -1}/>
                        <ListItemText primary={option}/>
                    </MenuItem>
                ))}
            </Select>
        </Box>
    );
}

function CustomGridToolbar({apiRef, ...props}) {
    return (
        <GridToolbarContainer {...props}>
            <GridToolbarColumnsButton/>
            <GridToolbarFilterButton/>
            <GridToolbarDensitySelector/>
            <GridToolbarExport
                csvOptions={{
                    fileName: `deposit-export-${new Date().toISOString().split('T')[0]}`,
                    utf8WithBom: true, // Adds BOM to support Excel and shows special characters correctly
                    getRowsToExport: () => apiRef.current.getAllRowIds() // Export all rows regardless of selected rows
                }}
                printOptions={{ disableToolbarButton: true }}
                apiRef={apiRef}
            />
        </GridToolbarContainer>
    )
}

function GridFilterDateTimeInput(props) {
    const {item, applyValue} = props;

    const handleFilterChange = (newValue) => {
        applyValue({...item, value: newValue});
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DateTimePicker
                value={item.value || null}
                autoFocus
                label="Date"
                timeSteps={{hours:1, minutes:1}}
                onChange={handleFilterChange}
                disableFuture
                minDate={dayjs().subtract(6, 'months')}
                slotProps={{
                    textField: {
                        variant: 'standard',
                    },
                    inputAdornment: {
                        sx: {
                            '& .MuiButtonBase-root': {
                                marginRight: -1,
                            },
                        },
                    },
                }}
            />
        </LocalizationProvider>
    );
}

function GridFilterDateInput(props) {
    const {item, applyValue} = props;

    const handleFilterChange = (newValue) => {
        applyValue({...item, value: newValue});
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
                value={item.value ? dayjs(item.value) : null}
                autoFocus
                label="Date"
                onChange={handleFilterChange}
                disableFuture
                minDate={dayjs().subtract(6, 'months')}
                slotProps={{
                    textField: {
                        variant: 'standard',
                    },
                    inputAdornment: {
                        sx: {
                            '& .MuiButtonBase-root': {
                                marginRight: -1,
                            },
                        },
                    },
                }}
            />
        </LocalizationProvider>
    );
}

const customLabeledNumericOperators = getGridNumericOperators().filter((operator) => ['=', '>', '>=', '<', '<='].includes(operator.value)).map((operator) => {
    switch (operator.value) {
        case '=':
            return {...operator, label: 'equals'};
        case '!=':
            return {...operator, label: 'does not equal'}
        case '>':
            return {...operator, label: 'is greater than'};
        case '>=':
            return {...operator, label: 'is greater than or equal to'};
        case '<':
            return {...operator, label: 'is less than'};
        case '<=':
            return {...operator, label: 'is less than or equal to'};
        default:
            return operator;
    }
});

const baseColumns = (theme, rows, showReasonColumn, showLockedColumn, truechecksConfig, tabStatus, depositColumnWidth, checkData) => [
    {
        field: "itemType",
        headerName: "Type",
        headerAlign: "center",
        align: "center",
        width: depositColumnWidth["Type"] ?? 60,
        renderCell: (params) => <ItemTypeComponent type={params.value}/>,
        filterable: true,
        filterOperators: [
            {
                label: 'is',
                value: 'is',
                getApplyFilterFn: (filterItem) => (itemType) => {
                    return !filterItem.value || filterItem.value === itemType
                },
                InputComponent: ItemTypeSelectFilter,
                getValueAsString: (value) => value === "00" ? "Cash" : "Check" ?? "Unknown"
            },
        ],
        valueFormatter: (value) => value === "00" ? "Cash" : "Check" ?? "Unknown",
    },
    showLockedColumn && {
        field: "locked",
        disableExport: true,
        headerName: "Locked",
        headerAlign: "center",
        width: depositColumnWidth["Locked"] ?? 80,
        filterable: false,
        renderCell: (params) => (
            <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                {params.value ? <ThemedLockPersonIcon/> : null}
            </Box>
        )
    },
    {
        field: "item",
        headerName: "Item",
        width: depositColumnWidth["Item"] ?? 80,
        filterable: true,
        filterOperators: [
            ...getGridStringOperators().filter(({value}) => ['equals', 'isAnyOf'].includes(value)).map(operator => {
                switch (operator.value) {
                    case 'equals':
                        return {...operator, label: 'is'}
                    default:
                        return operator
                }
            }),
            {
                label: 'transaction # is',
                value: 'transactionIs',
                getApplyFilterFn: (filterItem) => (itemNumber) =>
                    !filterItem.value || filterItem.value.length === 0 || filterItem.value.includes(itemNumber.split(':')[0]),
                InputComponent: GridFilterInputValue,
            },
            {
                label: 'transaction # is any of',
                value: 'transactionIsAnyOf',
                getApplyFilterFn: (filterItem) => (itemNumber) =>
                    !filterItem.value || filterItem.value.length === 0 || filterItem.value.includes(itemNumber.split(':')[0]),
                InputComponent: GridFilterInputMultipleValue,
            },
        ],
        sortComparator: (v1, v2) => {
            const [a1, a2] = v1.split(':').map(Number);
            const [b1, b2] = v2.split(':').map(Number);
            return a1 !== b1 ? a1 - b1 : a2 - b2;
        }
    },
    showReasonColumn && {
        field: "rejectReason",
        headerName: "Rejection Reason",
        width: depositColumnWidth["Rejection Reason"] ?? 150,
        renderCell: params => params.value || "N/A"
    },
    {
        field: "date",
        headerName: "Deposit Time",
        type: 'dateTime',
        width: depositColumnWidth["Deposit Time"] ?? 150,
        filterable: true,
        filterOperators: [
            {
                label: 'is exactly',
                value: 'isExactly',
                getApplyFilterFn: (filterItem) => (date) =>
                    !filterItem.value || filterItem.value.isSame(date, 'minute'),
                InputComponent: GridFilterDateTimeInput,
                getValueAsString: (value) => ReadableLocaleDate(value)
            },
            {
                label: 'is before',
                value: 'isBefore',
                getApplyFilterFn: (filterItem) => (date) =>
                    !filterItem.value || dayjs(ReadableLocaleDate(date)).isBefore(filterItem.value),
                InputComponent: GridFilterDateTimeInput,
                getValueAsString: (value) => ReadableLocaleDate(value)
            },
            {
                label: 'is after',
                value: 'isAfter',
                getApplyFilterFn: (filterItem) => (date) =>
                    !filterItem.value || dayjs(ReadableLocaleDate(date)).isAfter(filterItem.value),
                InputComponent: GridFilterDateTimeInput,
                getValueAsString: (value) => ReadableLocaleDate(value)
            },
            {
                label: 'is day of',
                value: 'isDayOf',
                getApplyFilterFn: (filterItem) => (date) =>
                    !filterItem.value || dayjs(ReadableLocaleDate(date)).isSame(filterItem.value, 'day'),
                InputComponent: GridFilterDateInput,
                getValueAsString: (value) => ReadableLocaleDate(value)
            },
        ],
        valueFormatter: ReadableLocaleDate,
    },
    {
        field: "terminalId",
        headerName: "Terminal",
        width: depositColumnWidth["Terminal"] ?? 80,
        filterable: true,
        filterOperators: [
            {
                label: 'is',
                value: 'is',
                getApplyFilterFn: (filterItem) => (terminal) =>
                    !filterItem.value || filterItem.value === terminal,
                InputComponent: TerminalIdSingleSelectFilter,
            },
            {
                label: 'is not',
                value: 'is not',
                getApplyFilterFn: (filterItem) => (terminal) =>
                    !filterItem.value || filterItem.value.length === 0 || !filterItem.value.includes(terminal),
                InputComponent: TerminalIdMultiSelectFilter,
            },
            {
                label: 'is any of',
                value: 'is any of',
                getApplyFilterFn: (filterItem) => (terminal) =>
                    !filterItem.value || filterItem.value.length === 0 || filterItem.value.includes(terminal),
                InputComponent: TerminalIdMultiSelectFilter,
            },
        ],
    },
    {
        field: "branch",
        headerName: "Branch",
        width: depositColumnWidth["Branch"] ?? 150,
        filterable: true,
        filterOperators: [
            {
                label: 'is',
                value: 'is',
                getApplyFilterFn: (filterBranch) => (branch) =>
                    !filterBranch.value || filterBranch.value === branch,
                InputComponent: BranchSingleSelectFilter
            },
            {
                label: 'is not',
                value: 'is not',
                getApplyFilterFn: (filterBranch) => (branch) =>
                    !filterBranch.value || filterBranch.value !== branch,
                InputComponent: BranchSingleSelectFilter
            },
            {
                label: 'is any of',
                value: 'is any of',
                getApplyFilterFn: (filterBranch) => (branch) =>
                    !filterBranch.value || filterBranch.value.length === 0 || filterBranch.value.includes(branch),
                InputComponent: BranchMultiSelectFilter
            }
        ]
    },
    {
        field: "micr",
        headerName: "MICR",
        width: depositColumnWidth["MICR"] ?? 280,
        filterable: true,
        filterOperators: getGridStringOperators().filter(({value}) => ['contains'].includes(value)),
        renderCell: (params) => (
            <Box display="flex" alignItems="center">
                {!params.row.validMicr && <Warning sx={{color: theme.palette.accent.error, marginRight: .5}}/>}
                <span>{params.value}</span>
            </Box>
        )
    },
    {
        field: "session",
        headerName: "Session",
        type: 'number',
        width: depositColumnWidth["Session"] ?? 100,
        filterable: true,
        filterOperators: customLabeledNumericOperators,
    },
    {
        field: "accountNumber",
        headerName: "Account",
        width: depositColumnWidth["Account"] ?? 125,
        filterable: true,
        filterOperators: [...getGridStringOperators().filter(({value}) => ['equals', 'isAnyOf'].includes(value)).map(operator => {
            switch (operator.value) {
                case 'equals':
                    return {...operator, label: 'is'}
                default:
                    return operator
            }
        }),
        ]
    },
    {
        field: "itemAmount",
        headerName: "Amount",
        type: "number",
        width: depositColumnWidth["Amount"] ?? 100,
        valueFormatter: (value) => ToCurrency(value),
        filterable: true,
        filterOperators: customLabeledNumericOperators
    },
    {
        field: "reviewState",
        headerName: "Status",
        width: depositColumnWidth["Status"] ?? 115,
        filterable: true,
        filterOperators: [
            {
                label: 'is',
                value: 'is',
                getApplyFilterFn: (filterItem) => (reviewState) =>
                    !filterItem.value || filterItem.value === reviewState,
                InputComponent: (params => <StringSingleSelectFilter {...params} column={"Status"}
                                                                     columnValues={['Pending', 'Approved', 'Rejected']}/>)
            },
            {
                label: 'is not',
                value: 'is not',
                getApplyFilterFn: (filterItem) => (reviewState) =>
                    !filterItem.value || filterItem.value.length === 0 || !filterItem.value.includes(reviewState),
                InputComponent: (params => <StringSingleSelectFilter {...params} column={"Status"}
                                                                     columnValues={['Pending', 'Approved', 'Rejected']}/>)
            },
            {
                label: 'is any of',
                value: 'is any of',
                getApplyFilterFn: (filterItem) => (reviewState) =>
                    !filterItem.value || filterItem.value.length === 0 || filterItem.value.includes(reviewState),
                InputComponent: ReviewStatusMultiSelectFilter
            }
        ]
    },
    truechecksConfig?.enable && {
        field: "truechecksRecommendedAction",
        headerName: "TrueChecks® Action",
        width: depositColumnWidth["TrueChecks® Action"] ?? 180,
        renderCell: (params => <TrueChecksActionCell action={params.value}/>),
        filterable: true,
        filterOperators: [
            {
                label: 'is',
                value: 'is',
                getApplyFilterFn: (filterItem) => (action) => !filterItem.value || filterItem.value === action,
                InputComponent: TrueChecksSingleSelectFilter
            },
            {
                label: 'is any of',
                value: 'is any of',
                getApplyFilterFn: (filterItem) => (action) =>
                    !filterItem.value || filterItem.value.length === 0 || filterItem.value.includes(action),
                InputComponent: TrueChecksMultiSelectFilter,
            },
        ],
    },
    tabStatus !== "Pending" && {
        field: "reviewTime",
        headerName: "Review Time",
        type: "dateTime",
        width: depositColumnWidth["Review Time"] ?? 150,
        filterable: true,
        filterOperators: [
            {
                label: 'is exactly',
                value: 'isExactly',
                getApplyFilterFn: (filterItem) => (date) =>
                    !filterItem.value || filterItem.value.isSame(date, 'minute'),
                InputComponent: GridFilterDateTimeInput,
                getValueAsString: (value) => ReadableLocaleDate(value)
            },
            {
                label: 'is before',
                value: 'isBefore',
                getApplyFilterFn: (filterItem) => (date) =>
                    !filterItem.value || dayjs(ReadableLocaleDate(date)).isBefore(filterItem.value),
                InputComponent: GridFilterDateTimeInput,
                getValueAsString: (value) => ReadableLocaleDate(value)
            },
            {
                label: 'is after',
                value: 'isAfter',
                getApplyFilterFn: (filterItem) => (date) =>
                    !filterItem.value || dayjs(ReadableLocaleDate(date)).isAfter(filterItem.value),
                InputComponent: GridFilterDateTimeInput,
                getValueAsString: (value) => ReadableLocaleDate(value)
            },
            {
                label: 'is day of',
                value: 'isDayOf',
                getApplyFilterFn: (filterItem) => (date) =>
                    !filterItem.value || dayjs(ReadableLocaleDate(date)).isSame(filterItem.value, 'day'),
                InputComponent: GridFilterDateInput,
                getValueAsString: (value) => ReadableLocaleDate(value)
            },
        ],
        valueFormatter: (value) => ReadableLocaleDate(value) 
    },
    truechecksConfig?.enable && {
        field: "truechecksRecommendedReason",
        headerName: "TrueChecks® Reason",
        width: depositColumnWidth["TrueChecks® Reason"] ?? 180,
        filterable: true,
        filterOperators: [...getGridStringOperators().filter(({value}) => ['contains', 'equals', 'isAnyOf'].includes(value)).map(operator => {
            switch (operator.value) {
                case 'equals':
                    return {...operator, label: 'is'}
                default:
                    return operator
            }
        })
        ]
    },
    tabStatus !== "Pending" && {
        field: "primaryReviewer",
        headerName: "Reviewer",
        width: depositColumnWidth["Reviewer"] ?? 200,
        filterable: true,
        filterOperators: getGridStringOperators().filter(({value}) => ['contains', 'isAnyOf'].includes(value))
    }
].filter(Boolean); // Filters out any undefined columns

export const DataTable = ({
                              tabStatus,
                              rows,
                              getSelectedCheck,
                              showLockedColumn,
                              showReasonColumn,
                              updateSortedIds,
                              selectedCheckId,
                              filterParams
                          }) => {
    const theme = useTheme();
    const {customerConfigs, apiCallSuccess, isLoadingCheckData, truechecksConfig, checkData} = useGlobalContext();
    const [columns, setColumns] = useState([]);
    const [pageSize, setPageSize] = useState(10);
    const [checksDensity, setChecksDensity] = useState(localStorage.getItem('checksDensity') || 'standard');
    const [columnVisibility, setColumnVisibility] = useState({});
    const [depositColumnWidth, setDepositColumnWidth] = useState(JSON.parse(localStorage.getItem('depositColumnWidth')) || {})
    const apiRef = useGridApiRef();

    useEffect(() => {
        const updateColumnsAndVisibility = () => {
            const currentVisibility = JSON.parse(localStorage.getItem("columnVisibility")) || {};
            let columns = baseColumns(theme, rows, showReasonColumn, showLockedColumn, customerConfigs?.truechecksConfig, tabStatus, depositColumnWidth, checkData);
            const userColumnOrder = JSON.parse(localStorage.getItem("userColumnOrder"));

            // If user has a custom column order, sort columns to match ensuring all columns are present in each
            if (columns.length === userColumnOrder?.length && columns.every((column) => userColumnOrder.includes(column.field))) {
                columns.sort((a, b) => userColumnOrder.indexOf(a.field) - userColumnOrder.indexOf(b.field));
            }

            setColumns(columns);
            setColumnVisibility(currentVisibility);
        };

        updateColumnsAndVisibility();
    }, [customerConfigs?.truechecksConfig, showLockedColumn, showReasonColumn, tabStatus, rows]);

    // If custom filter params are passed, set the filter model to match
    useEffect(() => {
        if (filterParams && apiRef.current) {
            apiRef.current.setFilterModel({
                items: [{...filterParams, operator: filterParams.operator || 'is' || 'contains'}],
            });
        }
    }, [filterParams]);

    useEffect(() => {
        getFilteredSortedRows();
    }, [apiRef.current]);

    // If selected check in the component is changed, update selected row in data grid to match and highlight
    useEffect(() => {
        if (selectedCheckId && apiRef.current) {
            apiRef.current.selectRow(selectedCheckId, true, true)
        }
    }, [selectedCheckId]);

    useEffect(() => {
        localStorage.setItem("depositColumnWidth", JSON.stringify(depositColumnWidth))
    }, [depositColumnWidth]);

    const updateColumnOrdering = () => {
        let columnOrder = apiRef.current.getAllColumns();
        columnOrder = columnOrder.map((column) => column.field);
        localStorage.setItem("userColumnOrder", JSON.stringify(columnOrder));
    }

    const updateStoredColumnWidth = (params) => {
        try {
            const { colDef, width } = params;
            const headerName = colDef?.headerName;
            setDepositColumnWidth({ ...depositColumnWidth, [headerName]: width });
        } catch (error) {
            console.error(error)
        }
    };

    const handleColumnVisibility = (newModel) => {
        localStorage.setItem("columnVisibility", JSON.stringify(newModel));
        setColumnVisibility(newModel);
    };

    const handleDensityChange = (newDensity) => {
        localStorage.setItem('checksDensity', newDensity);
        setChecksDensity(newDensity);
    };

    const NoRowDataDisplay = () => (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
            }}
        >
            {apiCallSuccess ? (
                <CheckCircleOutlineIcon fontSize="large"/>
            ) : (
                <ErrorOutlineOutlined fontSize="large"/>
            )}
            <Box sx={{mt: 1}}>{apiCallSuccess ? "No More Deposits" : "Unable to Retrieve Deposits"}</Box>
        </Box>
    );

    const getFilteredSortedRows = () => {
        setTimeout(() => {
            const sortedRows = apiRef.current ? gridFilteredSortedRowIdsSelector(apiRef.current.state, apiRef.current.instanceId) : [];
            updateSortedIds(sortedRows);
        }, 100);
    };

    return (
        <DataGridPro
            apiRef={apiRef}
            columnVisibilityModel={columnVisibility}
            onColumnVisibilityModelChange={handleColumnVisibility}
            columns={columns}
            slots={{toolbar: (props) => <CustomGridToolbar {...props} apiRef={apiRef} />, noRowsOverlay: NoRowDataDisplay}}
            density={checksDensity}
            onDensityChange={(newDensity) => handleDensityChange(newDensity)}
            getRowId={(row) => row.item}
            initialState={{
                sorting: {sortModel: [{field: "item", sort: "asc"}]},
            }}
            disableColumnPinning
            loading={isLoadingCheckData}
            onColumnOrderChange={updateColumnOrdering}
            onColumnWidthChange={updateStoredColumnWidth}
            onPageSizeChange={setPageSize}
            onRowClick={(selected) => getSelectedCheck(selected.row.item)}
            onSortModelChange={getFilteredSortedRows}
            onFilterModelChange={getFilteredSortedRows}
            pageSize={pageSize}
            pagination
            rows={rows}
            onRowCountChange={getFilteredSortedRows}
            pageSizeOptions={[5, 10, 25, 50, 100]}
            sx={{
                height: "calc(75vh - 100px)",
                '.MuiDataGrid-columnHeaderTitle': {
                    fontWeight: 'bold !important',
                },
                "&.MuiDataGrid-root .MuiDataGrid-cell:focus-within": {outline: "none !important"},
                "& .MuiDataGrid-container--top [role=row], & .MuiDataGrid-container--bottom [role=row]": {
                    backgroundColor: "primary.background",
                },
            }}
        />
    );
};